import { useEffect, useId, useMemo, useState } from 'react'
import { Alert, Button, Dialog, Field, FormLayout, Input, Select } from './common'
import { ASSIGNABLE_ROLES, ROLES } from '../constants/roles'
import { apiErrorMessage } from '../utils/apiErrorMessage'
import styles from './UserForm.module.css'

function getInitialValue(user) {
  return user ? {
    email: user.email || '',
    display_name: user.display_name || '',
    role: user.role || ROLES.VIEWER,
  } : {
    email: '',
    display_name: '',
    role: ROLES.VIEWER,
  }
}

// roleOptions lists the roles the caller may assign. The API refuses any role
// at or above the caller's own, so the picker must not offer one.
function UserForm({ user, onSubmit, onCancel, isSubmitting, error, roleOptions = ASSIGNABLE_ROLES }) {
  const formId = useId()
  const [formData, setFormData] = useState(() => getInitialValue(user))
  const isEditing = Boolean(user)

  useEffect(() => {
    setFormData(getInitialValue(user))
  }, [user])

  const dirty = useMemo(() => (
    JSON.stringify(formData) !== JSON.stringify(getInitialValue(user))
  ), [formData, user])

  const handleSubmit = (event) => {
    event.preventDefault()
    if (isEditing) {
      const updates = {}
      if (formData.display_name !== user.display_name) updates.display_name = formData.display_name
      if (formData.email !== user.email) updates.email = formData.email
      onSubmit(updates)
      return
    }
    onSubmit({ email: formData.email, display_name: formData.display_name, role: formData.role })
  }

  const handleChange = (event) => {
    const { name, value } = event.target
    setFormData((current) => ({ ...current, [name]: value }))
  }

  return (
    <Dialog
      open
      title={isEditing ? 'Edit user' : 'Invite user'}
      description={isEditing ? 'Update the operator’s display name.' : 'Studio will email an invitation to finish account setup.'}
      onClose={onCancel}
      footer={(
        <>
          {dirty ? <span className={styles.dirtyState} role="status">Unsaved changes</span> : null}
          <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>Cancel</Button>
          <Button type="submit" form={formId} loading={isSubmitting}>{isEditing ? 'Save' : 'Send invite'}</Button>
        </>
      )}
    >
      <form id={formId} onSubmit={handleSubmit}>
        <FormLayout>
          <Field label="Email" required={!isEditing}>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required={!isEditing}
              disabled={isEditing}
              autoComplete="email"
            />
          </Field>

          <Field label="Display Name" required={!isEditing}>
            <Input
              name="display_name"
              value={formData.display_name}
              onChange={handleChange}
              required={!isEditing}
              autoComplete="name"
            />
          </Field>

          {!isEditing ? (
            <Field label="Role" required>
              <Select name="role" value={formData.role} onChange={handleChange} required>
                {roleOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </Select>
            </Field>
          ) : null}

          {error ? <Alert variant="error">{apiErrorMessage(error, 'An error occurred')}</Alert> : null}
        </FormLayout>
      </form>
    </Dialog>
  )
}

export default UserForm
